import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { setActiveUserId, setActiveOrganizationId } from '../api/client';
import { logout as apiLogout, getMyOrganizations, switchOrganization as apiSwitchOrg, clearOrganization as apiClearOrg } from '../api/auth';
import type { UserOrganization } from '../api/auth';

export type UserRole = 'STUDENT' | 'TUTOR' | 'ADMIN' | 'PARENT';

interface User {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  satEnabled?: boolean;
  satJourneyMode?: 'SELF_SERVE' | 'TUTOR_LED' | null;
  phoneNumber?: string;
  phoneVerified?: boolean;
  phoneVerifiedAt?: string | null;
  phoneVerificationPending?: boolean;
  phoneVerificationCooldown?: number;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
  avatarUrl?: string | null;
}

interface Session {
  user: User;
  impersonatedBy?: string; // full name of the admin who started impersonation
  activeOrganization?: { id: string; name: string; role: string } | null;
}

interface AuthContextType {
  user: User | null;
  currentSession: Session | null;
  sessions: Session[];
  activeSessionIndex: number;
  login: (email: string, password: string) => Promise<User>;
  oauthLogin: (id: string, email: string, fullName: string, role: UserRole, satEnabled?: boolean, emailVerified?: boolean, emailVerifiedAt?: string | null) => void;
  register: (email: string, password: string, fullName: string, role?: UserRole, intent?: 'sat') => Promise<void>;
  logout: () => Promise<void>;
  switchSession: (index: number) => void;
  removeSession: (index: number) => void;
  addSession: (user: User, impersonatedBy?: string) => void;
  refreshUser: () => Promise<void>;
  isImpersonating: boolean;
  stopImpersonation: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Organization context
  activeOrganization: { id: string; name: string; role: string } | null;
  organizations: UserOrganization[];
  switchOrganization: (orgId: string) => Promise<void>;
  clearOrganization: () => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  persistOrganizationContext: (id: string, name: string, role: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function toUser(data: any): User {
  return {
    id: data.id,
    email: data.email,
    fullName: data.fullName,
    role: data.role,
    satEnabled: data.satEnabled ?? false,
    satJourneyMode: data.satJourneyMode ?? null,
    phoneNumber: data.phoneNumber || undefined,
    phoneVerified: data.phoneVerified ?? false,
    phoneVerifiedAt: data.phoneVerifiedAt || null,
    phoneVerificationPending: data.phoneVerificationPending ?? false,
    phoneVerificationCooldown: data.phoneVerificationCooldown ?? 0,
    emailVerified: data.emailVerified,
    emailVerifiedAt: data.emailVerifiedAt || null,
    avatarUrl: data.avatarUrl || null,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSessionIndex, setActiveSessionIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const queryClient = useQueryClient();
  
  // Refs to track current values for use in callbacks (avoids stale closures)
  const activeSessionIndexRef = useRef<number>(0);
  const sessionsRef = useRef<Session[]>([]);

  const clearPersistedSessions = () => {
    setSessions([]);
    setActiveSessionIndex(0);
    setOrganizations([]);
    activeSessionIndexRef.current = 0;
    sessionsRef.current = [];
    localStorage.removeItem('sessions');
    localStorage.removeItem('activeSessionIndex');
    setActiveUserId(null);
    setActiveOrganizationId(null);
  };

  /**
   * Persist session metadata (user info only — no tokens) to localStorage.
   * Also updates the module-level activeUserId in api/client so the
   * X-User-Id header is set correctly on all subsequent requests.
   */
  const persist = (updatedSessions: Session[], activeIndex: number) => {
    setSessions(updatedSessions);
    setActiveSessionIndex(activeIndex);
    activeSessionIndexRef.current = activeIndex; // Keep ref in sync
    sessionsRef.current = updatedSessions; // Keep ref in sync
    localStorage.setItem('sessions', JSON.stringify(updatedSessions));
    localStorage.setItem('activeSessionIndex', String(activeIndex));
    const active = updatedSessions[activeIndex];
    setActiveUserId(active ? active.user.id : null);
    // Sync org context to api client
    setActiveOrganizationId(active?.activeOrganization?.id ?? null);
  };

  // On mount: force re-login if old token-based localStorage keys are present
  // (migrating from Bearer-token to HTTP-only cookie auth). Otherwise restore
  // session metadata normally — cookies are already in the browser.
  useEffect(() => {
    if (
      localStorage.getItem('jwt_token') !== null ||
      localStorage.getItem('refresh_token') !== null
    ) {
      // Old session format — clear everything and redirect to /login
      localStorage.clear();
      setIsLoading(false);
      window.location.href = '/login';
      return;
    }

    const storedSessions = localStorage.getItem('sessions');
    const storedActiveIndex = localStorage.getItem('activeSessionIndex');

    if (!storedSessions) {
      setIsLoading(false);
      return;
    }

    try {
      const parsed = JSON.parse(storedSessions) as Session[];
      const idx = storedActiveIndex ? parseInt(storedActiveIndex, 10) : 0;
      const validIdx = Math.max(0, Math.min(idx, parsed.length - 1));

      if (parsed.length === 0) {
        clearPersistedSessions();
        setIsLoading(false);
        return;
      }

      persist(parsed, validIdx);

      void (async () => {
        try {
          const response = await apiClient.get('/auth/me');
          const updatedUser = toUser(response.data);
          const currentSession = parsed[validIdx];
          const validatedSessions = [...parsed];
          validatedSessions[validIdx] = { ...currentSession, user: updatedUser };
          persist(validatedSessions, validIdx);
        } catch {
          clearPersistedSessions();
        } finally {
          setIsLoading(false);
        }
      })();
    } catch {
      clearPersistedSessions();
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    const response = await apiClient.post('/auth/login', { email, password });
    const userData: User = toUser(response.data);
    const newSession: Session = { user: userData };
    let updatedSessions = [...sessions];
    const existingIndex = updatedSessions.findIndex(s => s.user.email === userData.email);
    if (existingIndex !== -1) {
      updatedSessions[existingIndex] = newSession;
      persist(updatedSessions, existingIndex);
    } else {
      updatedSessions.push(newSession);
      persist(updatedSessions, updatedSessions.length - 1);
    }
    return userData;
  };

  /**
   * Handle OAuth login (e.g., Google). The backend has already authenticated
   * the user and set the auth cookies. We just need to create the session.
   */
  const oauthLogin = (id: string, email: string, fullName: string, role: UserRole, satEnabled?: boolean, emailVerified?: boolean, emailVerifiedAt?: string | null) => {
    const userData: User = { id, email, fullName, role, satEnabled: satEnabled ?? false, emailVerified: emailVerified ?? false, emailVerifiedAt: emailVerifiedAt || null };
    const newSession: Session = { user: userData };
    let updatedSessions = [...sessions];
    const existingIndex = updatedSessions.findIndex(s => s.user.email === email);
    if (existingIndex !== -1) {
      updatedSessions[existingIndex] = newSession;
      persist(updatedSessions, existingIndex);
    } else {
      updatedSessions.push(newSession);
      persist(updatedSessions, updatedSessions.length - 1);
    }
  };

  const register = async (email: string, password: string, fullName: string, role: UserRole = 'STUDENT', intent?: 'sat') => {
    const response = await apiClient.post('/auth/register', { email, password, fullName, role, ...(intent ? { intent } : {}) });
    const userData: User = toUser(response.data);
    const newSession: Session = { user: userData };
    let updatedSessions = [...sessions];
    const existingIndex = updatedSessions.findIndex(s => s.user.email === userData.email);
    if (existingIndex !== -1) {
      updatedSessions[existingIndex] = newSession;
      persist(updatedSessions, existingIndex);
    } else {
      updatedSessions.push(newSession);
      persist(updatedSessions, updatedSessions.length - 1);
    }
  };

  const logout = async () => {
    // Use refs to avoid stale closures
    if (sessionsRef.current.length === 0) return;
    const currentIndex = activeSessionIndexRef.current;
    const currentSession = sessionsRef.current[currentIndex];
    if (currentSession) {
      await apiLogout(currentSession.user.id);
    }
    const updatedSessions = sessionsRef.current.filter((_, idx) => idx !== currentIndex);
    const newActiveIndex = updatedSessions.length > 0
      ? Math.min(currentIndex, updatedSessions.length - 1)
      : 0;
    persist(updatedSessions, newActiveIndex);
  };

  const switchSession = (index: number) => {
    // Use ref to avoid stale closure
    if (index >= 0 && index < sessionsRef.current.length) {
      // Clear all cached queries so the new user doesn't see stale data
      queryClient.clear();
      persist(sessionsRef.current, index);
    }
  };

  const removeSession = (index: number) => {
    // Use refs to avoid stale closures
    if (index < 0 || index >= sessionsRef.current.length) return;
    const updatedSessions = sessionsRef.current.filter((_, idx) => idx !== index);
    const currentIndex = activeSessionIndexRef.current;
    let newActiveIndex = currentIndex;
    if (index === currentIndex) {
      newActiveIndex = updatedSessions.length > 0 ? 0 : -1;
    } else if (index < currentIndex) {
      newActiveIndex = currentIndex - 1;
    }
    persist(updatedSessions, newActiveIndex);
  };

  const addSession = (user: User, impersonatedBy?: string) => {
    const newSession: Session = { user, ...(impersonatedBy ? { impersonatedBy } : {}) };
    // Use ref to avoid stale closure on sessions
    let updatedSessions = [...sessionsRef.current];
    const existingIndex = updatedSessions.findIndex(s => s.user.email === user.email);
    if (existingIndex !== -1) {
      updatedSessions[existingIndex] = newSession;
      persist(updatedSessions, existingIndex);
    } else {
      updatedSessions.push(newSession);
      persist(updatedSessions, updatedSessions.length - 1);
    }
  };

  const refreshUser = async () => {
    // Use ref to avoid stale closure on sessions
    if (sessionsRef.current.length === 0) return;
    try {
      const response = await apiClient.get('/auth/me');
      const updatedUser: User = toUser(response.data);
      
      // Use functional update to avoid stale closure, and use ref for current index
      setSessions(prevSessions => {
        const currentIndex = activeSessionIndexRef.current;
        const currentSession = prevSessions[currentIndex];
        if (!currentSession) return prevSessions;
        const updatedSession: Session = { ...currentSession, user: updatedUser };
        const updatedSessions = [...prevSessions];
        updatedSessions[currentIndex] = updatedSession;
        localStorage.setItem('sessions', JSON.stringify(updatedSessions));
        sessionsRef.current = updatedSessions; // Keep ref in sync
        setActiveUserId(updatedUser.id);
        return updatedSessions;
      });
    } catch (error) {
      clearPersistedSessions();
      console.error('Failed to refresh user:', error);
    }
  };

  const stopImpersonation = async () => {
    // Use refs to avoid stale closures
    const currentIndex = activeSessionIndexRef.current;
    const currentSession = sessionsRef.current[currentIndex];
    if (currentSession) {
      try {
        await apiLogout(currentSession.user.id);
      } catch {
        // Ignore errors - clean up locally regardless
      }
    }
    const updatedSessions = sessionsRef.current.filter((_, idx) => idx !== currentIndex);
    const newActiveIndex = updatedSessions.length > 0 ? 0 : -1;
    persist(updatedSessions, newActiveIndex);
    window.location.href = '/admin/users';
  };

  // Fetch organizations when the active session changes
  const refreshOrganizations = async () => {
    if (sessionsRef.current.length === 0) {
      setOrganizations([]);
      return;
    }
    try {
      const orgs = await getMyOrganizations();
      setOrganizations(orgs);
    } catch {
      setOrganizations([]);
    }
  };

  // Load organizations on mount and when active session changes
  useEffect(() => {
    if (sessions.length > 0 && !isLoading) {
      refreshOrganizations();
    }
  }, [activeSessionIndex, isLoading]);

  const switchOrganization = async (orgId: string) => {
    const result = await apiSwitchOrg(orgId);
    const orgContext = { id: result.organizationId, name: result.organizationName, role: result.organizationRole };

    // Update the active session with the org context
    const currentIndex = activeSessionIndexRef.current;
    const updatedSessions = [...sessionsRef.current];
    updatedSessions[currentIndex] = { ...updatedSessions[currentIndex], activeOrganization: orgContext };
    persist(updatedSessions, currentIndex);
    queryClient.clear();
  };

  // Directly persist org context into the session without an API call.
  // Used after invite acceptance where the backend already issued a new JWT with org claims.
  const persistOrganizationContext = (id: string, name: string, role: string) => {
    const orgContext = { id, name, role };
    const currentIndex = activeSessionIndexRef.current;
    const updatedSessions = [...sessionsRef.current];
    updatedSessions[currentIndex] = { ...updatedSessions[currentIndex], activeOrganization: orgContext };
    persist(updatedSessions, currentIndex);
    queryClient.clear();
  };

  const clearOrganization = async () => {
    await apiClearOrg();

    // Clear org context from active session
    const currentIndex = activeSessionIndexRef.current;
    const updatedSessions = [...sessionsRef.current];
    updatedSessions[currentIndex] = { ...updatedSessions[currentIndex], activeOrganization: null };
    persist(updatedSessions, currentIndex);
    queryClient.clear();
  };

  const activeSession = sessions[activeSessionIndex] || null;
  const isImpersonating = !!(activeSession?.impersonatedBy);

  const value = {
    user: activeSession ? activeSession.user : null,
    currentSession: activeSession,
    sessions,
    activeSessionIndex,
    login,
    oauthLogin,
    register,
    logout,
    switchSession,
    removeSession,
    addSession,
    refreshUser,
    isImpersonating,
    stopImpersonation,
    isAuthenticated: !!activeSession,
    isLoading,
    // Organization context
    activeOrganization: activeSession?.activeOrganization ?? null,
    organizations,
    switchOrganization,
    clearOrganization,
    refreshOrganizations,
    persistOrganizationContext,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
